<script setup lang="ts">
import { computed, ref } from 'vue'
import { type Notification } from '@/stores/notifications/store'
import type { IconName } from '@/components/Base/Icons/icons'
import Icon from '@/components/Base/BaseIcon.vue'
import AppButtonCircular from '@/components/Base/Buttons/AppButtonCircular.vue'

const props = defineProps<Notification>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const isHovered = ref(false)

const isFocused = ref(false)

const isTapped = ref(false)

const isTimeoutRunning = computed(
  () => !isHovered.value && !isFocused.value && !isTapped.value
)

const iconName = computed<IconName>(() => {
  switch (props.type) {
    case 'error':
      return 'ErrorIcon'

    case 'info':
      return 'InfoIcon'

    case 'success':
      return 'SuccessIcon'
  }
})
</script>

<template>
  <div
    class="w-[384px] bg-neutral-0 rounded-lg shadow-toast flex flex-col overflow-hidden border border-neutral-200 drop-shadow-sm"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @focus="isFocused = true"
    @blur="isFocused = false"
    @touchmove="isTapped = true"
    @touchend="isTapped = false"
  >
    <div class="p-4 flex gap-4 justify-between">
      <div class="flex gap-3">
        <div>
          <Icon :name="iconName" size="1.5rem" />
        </div>

        <div class="flex flex-col gap-1 pt-1">
          <span as="span" size="sm" weight="medium" class="text-sm font-medium" v-html="title">
          </span>

          <span v-if="message" class="text-sm font-regular">
            {{ message }}
          </span>
        </div>
      </div>

      <div>
        <AppButtonCircular
          icon-name="Close"
          size="xs"
          icon-size="1.5rem"
          data-test="close-notification-button"
          @click="emit('close')"
        />
      </div>
    </div>

    <div
      role="progressbar"
      aria-label="notification close duration"
      class="countdown-bar-animation w-full h-1 mt-auto"
      :class="{
        'bg-primary-700': type === 'info',
        'bg-danger-700': type === 'error',
        'bg-success-700': type === 'success',
      }"
      :style="{
        animationDuration: `${duration}ms`,
        animationPlayState: isTimeoutRunning ? 'running' : 'paused',
      }"
      @animationend="emit('close')"
    />
  </div>
</template>

<style scoped lang="scss">
@keyframes scale-x-down {
  0% {
    // Animating "transform" is more performant than animating "width" https://web.dev/articles/animations-guide#triggers
    transform: scaleX(1);
  }
  100% {
    transform: scaleX(0);
  }
}
.countdown-bar-animation {
  animation: scale-x-down linear 1 forwards;
  transform-origin: left;
}
</style>
